import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, calcLive, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import StatsCards from "./StatsCards";
import TradesTable from "./TradesTable";
import TradeDialog from "./TradeDialog";
import JournalDialog from "./JournalDialog";
import CoachInsights from "./CoachInsights";
import ChangePasswordDialog from "./ChangePasswordDialog";
import Header from "./dashboard/Header";
import FilterBar, { ALL, EMPTY_FILTERS } from "./dashboard/FilterBar";
import DeleteConfirmDialog from "./dashboard/DeleteConfirmDialog";
import { usePrices, useSymbols } from "./dashboard/usePrices";
import { Button } from "../components/ui/button";
import { Plus, Download, Upload } from "lucide-react";
import { toast } from "sonner";

const OUTLINE_BTN = "bg-transparent border-[#27272A] text-[#A1A1AA] hover:bg-[#1A1B23] hover:text-white rounded-sm";

function filterParams(filters) {
  const params = {};
  if (filters.broker !== ALL) params.broker = filters.broker;
  if (filters.category !== ALL) params.category = filters.category;
  if (filters.status !== ALL) params.status = filters.status;
  if (filters.search) params.search = filters.search;
  return params;
}

function downloadBlob(data, filename) {
  const url = URL.createObjectURL(new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

const dateKey = (t) => t.exit_date || t.date_of_purchase || "";
const SORTERS = {
  date_asc: (a, b, pl) => dateKey(a).localeCompare(dateKey(b)) || (a.created_at || "").localeCompare(b.created_at || ""),
  pl_desc: (a, b, pl) => pl(b) - pl(a),
  pl_asc: (a, b, pl) => pl(a) - pl(b),
  broker_asc: (a, b) => (a.broker || "").localeCompare(b.broker || "") || dateKey(b).localeCompare(dateKey(a)),
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [trades, setTrades] = useState([]);
  const [stats, setStats] = useState(null);
  const [reference, setReference] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sortBy, setSortBy] = useState("date_desc");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [journalTrade, setJournalTrade] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const fileInputRef = useRef(null);

  const symbols = useSymbols(trades);
  const { prices, fetching, lastFetch, cacheDate, fetchPrices } = usePrices(symbols);

  // "Latest-wins" guard: only the newest loadAll() response is applied
  const loadReqId = useRef(0);
  const loadAll = useCallback(async () => {
    const reqId = ++loadReqId.current;
    try {
      const [tRes, sRes] = await Promise.all([api.get("/trades", { params: filterParams(filters) }), api.get("/stats/summary")]);
      if (reqId !== loadReqId.current) return;
      setTrades(tRes.data);
      setStats(sRes.data);
    } catch (e) {
      if (reqId !== loadReqId.current) return;
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    }
  }, [filters]);

  useEffect(() => {
    api.get("/reference").then((r) => setReference(r.data)).catch(() => toast.error("Failed to load reference data"));
  }, []);

  // Debounce search input (300ms) — non-search filters apply instantly
  useEffect(() => {
    if (!filters.search) { loadAll(); return; }
    const t = setTimeout(loadAll, 300);
    return () => clearTimeout(t);
  }, [loadAll, filters.search]);

  const tradePL = useCallback((t) => {
    if (t.status === "CLOSED" && t.exit_rate != null) {
      return (t.transaction === "BOUGHT" ? 1 : -1) * (t.exit_rate - t.rate) * t.quantity;
    }
    const last = prices[t.stock?.toUpperCase()]?.last;
    return last ? calcLive(t, last).livePL ?? 0 : 0;
  }, [prices]);

  const totalLivePL = useMemo(
    () => trades.filter((t) => t.status !== "CLOSED").reduce((sum, t) => sum + tradePL(t), 0),
    [trades, tradePL],
  );

  const sortedTrades = useMemo(() => {
    const sorter = SORTERS[sortBy];
    return sorter ? [...trades].sort((a, b) => sorter(a, b, tradePL)) : trades; // server already returns date_desc
  }, [trades, sortBy, tradePL]);

  const onTradeSaved = useCallback(async () => {
    await loadAll();
    fetchPrices();
  }, [loadAll, fetchPrices]);

  async function doDelete() {
    try {
      await api.delete(`/trades/${confirmDel.id}`);
      toast.success("Trade deleted");
      setConfirmDel(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    }
  }

  async function doExport() {
    try {
      const res = await api.get("/trades/export", { responseType: "blob" });
      downloadBlob(res.data, `trades_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) {
      toast.error("Export failed");
    }
  }

  async function doImport(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/trades/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} trades${data.errors.length ? ` (${data.errors.length} errors)` : ""}`);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally {
      e.target.value = "";
    }
  }

  return (
    <div className="min-h-screen bg-[#08080A] text-white" data-testid="dashboard">
      <Header user={user} priceState={{ cacheDate, lastFetch, fetching }} onRefresh={() => fetchPrices(true)}
        onChangePassword={() => setPwOpen(true)} onLogout={logout} />

      <main className="px-4 sm:px-6 py-6 space-y-6 max-w-[1600px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#71717A] font-semibold">Portfolio</div>
            <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Dashboard</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={doImport} className="hidden" data-testid="import-input" />
            <Button data-testid="import-btn" variant="outline" onClick={() => fileInputRef.current?.click()} className={OUTLINE_BTN}>
              <Upload className="h-3.5 w-3.5 mr-2" /> <span className="text-xs">Import</span>
            </Button>
            <Button data-testid="export-btn" variant="outline" onClick={doExport} className={OUTLINE_BTN}>
              <Download className="h-3.5 w-3.5 mr-2" /> <span className="text-xs">Export</span>
            </Button>
            <Button data-testid="new-trade-btn" onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-[#007AFF] hover:bg-[#007AFF]/90 text-white rounded-sm">
              <Plus className="h-4 w-4 mr-2" /> New Trade
            </Button>
          </div>
        </div>

        <StatsCards stats={stats} totalLivePL={totalLivePL} />
        <CoachInsights />
        <FilterBar filters={filters} setFilters={setFilters} sortBy={sortBy} setSortBy={setSortBy} reference={reference} />
        <TradesTable trades={sortedTrades} prices={prices} onEdit={(t) => { setEditing(t); setDialogOpen(true); }} onDelete={setConfirmDel} onJournal={setJournalTrade} />
      </main>

      <TradeDialog open={dialogOpen} onClose={() => setDialogOpen(false)} initial={editing} reference={reference} onSaved={onTradeSaved} />
      <JournalDialog open={!!journalTrade} onClose={() => setJournalTrade(null)} trade={journalTrade} onSaved={loadAll} />
      <DeleteConfirmDialog trade={confirmDel} onCancel={() => setConfirmDel(null)} onConfirm={doDelete} />
      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}
